/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useEffect } from 'react'
import { matchPath, matchRoutes, useLocation } from 'react-router-dom'
import { last } from 'lodash'

import { loginUrl } from '@modules/auth/urls'
import { useConfig } from '@modules/cui/ConfigContext'
import { startRouteChange } from '@modules/utils/apm'
import routerRef from '@modules/routing/router'

import { applyGoogleTrackingV4 } from '@/apps/userconsole/lib/googleTracking'
import { registerUrl } from '@/lib/urlBuilder'

import type React from 'react'
import type { Location } from 'react-router-dom'

const AppRouterEvents: React.FunctionComponent = () => {
  const googleTrackingId = useConfig('GOOGLE_ANALYTICS_TRACKING_ID_V4')

  const location = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
    document.querySelector<HTMLElement>(`[autofocus]`)?.focus()

    trackRouteChange(location)

    if (googleTrackingId && isGoogleTrackingV4EnabledForUrl(location)) {
      applyGoogleTrackingV4()
    }
  }, [location, googleTrackingId])

  return null
}

function trackRouteChange(location: Location) {
  const { routes } = routerRef.current || {}

  const matches = matchRoutes(routes || [], location)
  const match = last(matches)

  if (!match) {
    return
  }

  const { params } = match

  startRouteChange(location.pathname, params)
}

function isGoogleTrackingV4EnabledForUrl(location: Location) {
  return matchPath(loginUrl(), location.pathname) || matchPath(registerUrl(), location.pathname)
}

export default AppRouterEvents
